// The NPC roster — the single list of every NPC spec, split out from the manager
// (index.ts) so the **server** can import the specs' plain data (spawn point,
// combat block, roam bounds) without pulling in the DOM-touching client manager.
//
// NPCs are CONTENT: a kind is authored as JSON (content/npcs/<id>.json — stats +
// blocks + talk graph — plus a model doc in content/models/); PLACEMENT lives in
// the map docs' `npcs` arrays (plan 21). src/content/load.ts validates + expands
// each map spawn into a full NpcSpec. Add an NPC kind = two JSON files + `bun
// run gen`; place it in a map doc (or the #/world editor). Spawn ids must stay
// stable — persisted npc death/respawn state is keyed by them.

import type { NpcSpec } from "./define.ts";
import { loadedNpcs, loadedNpcsByMap } from "../content/load.ts";

/** Every spawn across every map (kind tooling + the migration guard). Runtime
 *  consumers pick their map's list via NPCS_BY_MAP. */
export const NPCS: NpcSpec[] = loadedNpcs;

/** Per-map spec lists — the server spawns each map's list into its MapRt; the
 *  client builds only its active map's list. */
export const NPCS_BY_MAP: Map<string, NpcSpec[]> = loadedNpcsByMap;

/** An NPC is server-driven (authoritative position + combat/talk streamed to
 *  every client) iff it moves, fights, or talks — i.e. it has a `roam`, `combat`,
 *  `talk`, `flee`, or `territory` block. A talkable NPC needs the server to
 *  coordinate the conversation (halt its roam, face the talker, mark it busy for
 *  others), so `talk` opts it in too; a territorial NPC needs the server running
 *  its aggro/leash AI even if it never roams. Stationary, non-interactive NPCs
 *  are placed deterministically on every client and need no networking. */
export function isDynamic(spec: NpcSpec): boolean {
  return !!(spec.roam || spec.combat || spec.talk || spec.flee || spec.territory);
}

/** One spec per display name. Wildlife places many spawns of the same model
 *  (fox1/fox2, rabbit1/2/3…) — tooling that lists NPC *kinds* (the studio
 *  catalog, the combat sandbox) wants each family once. */
export function npcKinds(): NpcSpec[] {
  const seen = new Set<string>();
  const out: NpcSpec[] = [];
  for (const spec of NPCS) {
    if (seen.has(spec.name)) continue;
    seen.add(spec.name);
    out.push(spec);
  }
  return out;
}
